import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ChartLine } from '@phosphor-icons/react';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { openConsentPreferences } from '@/lib/consent';

const CONTENT = {
  pt: {
    title: 'Política de Cookies',
    version: 'Versão 1.0 (rascunho) — última atualização: junho de 2026. Este documento é um rascunho técnico e carece de validação jurídica [PENDENTE DE PREENCHIMENTO/VALIDAÇÃO].',
    intro: 'Usamos cookies e tecnologias semelhantes (localStorage/sessionStorage) para fazer a aplicação funcionar e, com o teu consentimento, para análise e marketing. Os itens essenciais estão sempre ativos; os restantes ficam bloqueados até aceitares no banner de consentimento. Podes alterar a tua escolha a qualquer momento através do botão "Gerir cookies".',
    manage: 'Gerir cookies',
    back: 'Voltar',
    headers: ['Nome', 'Tipo', 'Categoria', 'Finalidade', 'Duração'],
    catLabels: { essential: 'Essencial', analytics: 'Análise', marketing: 'Marketing' },
    rows: [
      ['session_token', 'Cookie', 'essential', 'Mantém a sessão autenticada.', '7 dias'],
      ['fh_consent', 'localStorage', 'essential', 'Guarda a tua decisão de consentimento.', 'Persistente'],
      ['funnelhack_lng', 'localStorage', 'essential', 'Guarda o idioma escolhido.', 'Persistente'],
      ['tour_done_*', 'localStorage', 'essential', 'Lembra os tours de onboarding já vistos.', 'Persistente'],
      ['fh_visitor_id', 'localStorage', 'analytics', 'Identificador aleatório de visitante para métricas de visitas. Só é criado com consentimento.', 'Persistente'],
      ['fh_visit_sent', 'sessionStorage', 'analytics', 'Evita pings de visita duplicados na mesma sessão.', 'Sessão'],
      ['fh_attr', 'localStorage', 'marketing', 'Atribuição de primeiro toque (UTM) para medir a origem de registos. Só é criado com consentimento.', 'Até ao registo/login'],
    ],
    note: 'Nota: não utilizamos ferramentas de análise de terceiros. As métricas de visitas são próprias, usam apenas IP truncado e classe de dispositivo, e só são recolhidas com consentimento de "Análise" — o servidor rejeita eventos de tracking sem uma decisão de consentimento válida.',
  },
  en: {
    title: 'Cookie Policy',
    version: 'Version 1.0 (draft) — last updated: June 2026. This document is a technical draft pending legal validation [PENDENTE DE PREENCHIMENTO/VALIDAÇÃO].',
    intro: 'We use cookies and similar technologies (localStorage/sessionStorage) to make the app work and, with your consent, for analytics and marketing. Essential items are always active; the rest stay blocked until you accept them in the consent banner. You can change your choice at any time via the "Manage cookies" button.',
    manage: 'Manage cookies',
    back: 'Back',
    headers: ['Name', 'Type', 'Category', 'Purpose', 'Duration'],
    catLabels: { essential: 'Essential', analytics: 'Analytics', marketing: 'Marketing' },
    rows: [
      ['session_token', 'Cookie', 'essential', 'Keeps your authenticated session.', '7 days'],
      ['fh_consent', 'localStorage', 'essential', 'Stores your consent decision.', 'Persistent'],
      ['funnelhack_lng', 'localStorage', 'essential', 'Stores your chosen language.', 'Persistent'],
      ['tour_done_*', 'localStorage', 'essential', 'Remembers which onboarding tours you have seen.', 'Persistent'],
      ['fh_visitor_id', 'localStorage', 'analytics', 'Random visitor identifier for visit metrics. Only created with consent.', 'Persistent'],
      ['fh_visit_sent', 'sessionStorage', 'analytics', 'Prevents duplicate visit pings in the same session.', 'Session'],
      ['fh_attr', 'localStorage', 'marketing', 'First-touch (UTM) attribution to measure signup sources. Only created with consent.', 'Until signup/login'],
    ],
    note: 'Note: we do not use third-party analytics tools. Visit metrics are first-party, use only a truncated IP and device class, and are only collected with "Analytics" consent — the server rejects tracking events without a valid consent decision.',
  },
};

const catColor = {
  essential: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
  analytics: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
  marketing: 'text-purple-400 bg-purple-400/10 border-purple-400/20',
};

export default function CookiePolicy() {
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || 'en').slice(0, 2);
  const c = CONTENT[lang] || CONTENT.en;

  return (
    <div data-testid="cookie-policy-page" className="min-h-screen bg-[#050505] text-white px-6 py-10">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" data-testid="policy-back-link" className="flex items-center gap-2 text-white/60 hover:text-white transition text-sm">
            <ArrowLeft size={16} /> {c.back}
          </Link>
          <LanguageSwitcher variant="compact" />
        </div>
        <div className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 bg-gradient-to-br from-[#2563EB] to-[#3B82F6] rounded-lg flex items-center justify-center">
            <ChartLine size={18} weight="bold" className="text-white" />
          </div>
          <span className="text-white/80 font-[Outfit] font-bold">FunnelHack AI</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold font-[Outfit] mb-3">{c.title}</h1>
        <p className="text-xs text-amber-400/90 bg-amber-400/10 border border-amber-400/20 rounded-lg px-3 py-2 mb-6">{c.version}</p>
        <p className="text-sm text-white/60 leading-relaxed mb-8">{c.intro}</p>

        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-sm" data-testid="cookie-table">
            <thead>
              <tr className="bg-white/5 text-left">
                {c.headers.map((h) => (
                  <th key={h} className="px-4 py-3 font-semibold text-white/80 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {c.rows.map(([name, type, cat, purpose, duration]) => (
                <tr key={name} className="border-t border-white/10 align-top">
                  <td className="px-4 py-3 font-mono text-xs text-white/90 whitespace-nowrap">{name}</td>
                  <td className="px-4 py-3 text-white/60 whitespace-nowrap">{type}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${catColor[cat]}`}>{c.catLabels[cat]}</span>
                  </td>
                  <td className="px-4 py-3 text-white/60">{purpose}</td>
                  <td className="px-4 py-3 text-white/60 whitespace-nowrap">{duration}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-white/40 mt-4">{c.note}</p>

        <button
          data-testid="manage-cookies-btn"
          onClick={openConsentPreferences}
          className="mt-6 px-5 py-2.5 rounded-full border border-white/25 text-sm text-white/80 hover:text-white hover:border-white/50 transition"
        >
          {c.manage}
        </button>
      </div>
    </div>
  );
}
